import { useId } from "react";

import { useFormContext } from "react-hook-form";

import { FormField } from "@ps-design-system/components/Form";
import { InputField } from "@ps-design-system/components/Input";
import { Switch } from "@ps-design-system/components/Switch";
import { Text } from "@ps-design-system/components/Text";

import { useIntl } from "react-intl-compat";

import { MAX_GRATUITY_RATE, MIN_GRATUITY_RATE } from "../ListingFsvForm.consts";
import type { ListingFsvFormValues } from "../ListingFsvForm.schema";
import { isBlankAmount } from "../ListingFsvForm.utils";

type GratuityRateFieldProps = {
  onBlur: () => void;
};

/** The FSV gratuity toggle and rate input. */
const GratuityRateField = ({ onBlur }: GratuityRateFieldProps) => {
  const intl = useIntl();
  const toggleLabelId = useId();
  const { control, getValues, setValue } =
    useFormContext<ListingFsvFormValues>();

  // Clamp the rate into range; blank is left for the form's validation
  const handleBlur = () => {
    const gratuityRate = getValues("gratuityRate");
    if (isBlankAmount(gratuityRate)) {
      onBlur();
      return;
    }

    const clamped = Math.min(
      Math.max(Number(gratuityRate), MIN_GRATUITY_RATE),
      MAX_GRATUITY_RATE,
    );
    if (clamped !== Number(gratuityRate)) {
      setValue("gratuityRate", clamped, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    onBlur();
  };

  return (
    <FormField
      control={control}
      name="hasGratuity"
      render={({ field }) => (
        <div className="tw-mb-16" data-testid="fsv-form-gratuity-field">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-16 tw-pt-8">
            <Text id={toggleLabelId} typeface="body-2-m" element="p">
              {intl.formatMessage({
                id: "hosting.listing_fsv.gratuity_toggle_label",
                defaultMessage: "Do you charge gratuity?",
              })}
            </Text>
            <Switch
              aria-labelledby={toggleLabelId}
              checked={field.value}
              onCheckedChange={field.onChange}
              data-testid="fsv-form-gratuity-toggle"
            />
          </div>

          {field.value && (
            // InputField overrides its own onBlur with the form's, so listen here
            <div className="tw-mt-16" onBlur={handleBlur}>
              <InputField
                fullWidth
                name="gratuityRate"
                label={intl.formatMessage({
                  id: "hosting.listing_fsv.gratuity_label",
                  defaultMessage: "Gratuity",
                })}
                subcopy={intl.formatMessage({
                  id: "hosting.listing_fsv.gratuity_subcopy",
                  defaultMessage: "100% of gratuity goes to you.",
                })}
                type="number"
                min={MIN_GRATUITY_RATE}
                max={MAX_GRATUITY_RATE}
                step={1}
                suffix="%"
                testId="fsv-form-gratuity-rate-input"
              />
            </div>
          )}
        </div>
      )}
    />
  );
};

export { GratuityRateField };
